import { STATUS, getStatusId } from '@btime/constants'
import { useNotification } from '@btime/react-messager'
import { useTranslation } from 'react-i18next'

import { useCloseModalOrNavigation } from '../../../../components/modals/ModalsContext'
import { useIsPermalinkPage } from '../../../../utils/session/permalinkToken'
import { useSaveServiceOrder } from './saveServiceOrder'
import { useIsBulkExecution, useServiceOrder } from '../_load/ServiceOrderLoader'
import { getQueryParam } from '../../../../utils/helpers/url'
import { useOpenEditServiceOrdersStatusModal } from '../../../../utils/routes/modals/modalsActions'
import { useConfig } from '../../../../containers/configs/_load/ConfigsLoader'

const useGoToResumeOrCustomPDF = () => {
  const config = useConfig('redirect_to_custom_pdf_on_finish')
  const navigate = useCloseModalOrNavigation()
  return serviceOrder => {
    const redirectToCustomPdfOnFinish = config?.value?.find(item => item.checklistId === serviceOrder.checklistId)
    const path = redirectToCustomPdfOnFinish?.path

    redirectToCustomPdfOnFinish
      ? navigate(`/pdfs/${path}/${serviceOrder.id}`)
      : navigate(`/service-orders/${serviceOrder.id}/resume`)
  }
}

export const usePauseServiceOrderRequest = () => {
  const { t } = useTranslation('serviceOrders')
  const navigate = useCloseModalOrNavigation()
  const serviceOrder = useServiceOrder()
  const notify = useNotification()
  const onOpenEditStatusModal = useOpenEditServiceOrdersStatusModal()

  const onSuccess = () => {
    notify.success({ title: t('execution.paused') })
    navigate(`/service-orders`)
  }

  return [
    () => {
      onOpenEditStatusModal({ ids: [serviceOrder.id], statusName: 'pause', onSuccess })
    },
    { loading: false }
  ]
}

export const useReasonPauseServiceOrderRequest = () => {
  const { t } = useTranslation('serviceOrders')
  const navigate = useCloseModalOrNavigation()
  const notify = useNotification()

  return useSaveServiceOrder({
    newStatusId: getStatusId(STATUS.PAUSE),
    shouldValidate: false,
    onSuccess: () => {
      notify.success({ title: t('execution.paused') })
      navigate(`/service-orders`)
    }
  })
}

export const useCompleteServiceOrderRequest = () => {
  const { t } = useTranslation('serviceOrders')
  const serviceOrder = useServiceOrder()
  const isBulk = useIsBulkExecution()
  const navigate = useCloseModalOrNavigation()
  const notify = useNotification()
  const isPublic = useIsPermalinkPage()
  const goToResumeOrCustomPDF = useGoToResumeOrCustomPDF()

  return useSaveServiceOrder({
    newStatusId: getStatusId(STATUS.COMPLETED),
    shouldValidate: true,
    onSuccess: () => {
      notify.success({ title: t('execution.completed') })

      // Execução em massa não tem uma única atividade para redirecionar ao
      // "resume" — fica na lista, que já é atualizada pelo refetch da mutation.
      if (isBulk) return navigate(`/service-orders`)

      if (isPublic) {
        navigate(`/public/service-orders/successfully-executed`)
      } else {
        goToResumeOrCustomPDF(serviceOrder)
      }
    }
  })
}

export const useUpdateServiceOrderRequest = () => {
  const { t } = useTranslation('serviceOrders')
  const serviceOrder = useServiceOrder()
  const isBulk = useIsBulkExecution()
  const navigate = useCloseModalOrNavigation()
  const notify = useNotification()
  const ignoreValidation = getQueryParam('ignoreValidation')
  const goToResumeOrCustomPDF = useGoToResumeOrCustomPDF()

  return useSaveServiceOrder({
    shouldValidate: !ignoreValidation,
    onSuccess: () => {
      notify.success({ title: t('execution.saved') })
      if (isBulk) return navigate(`/service-orders`)
      goToResumeOrCustomPDF(serviceOrder)
    }
  })
}
